import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, FileText, Link2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { EmirateSelect, EmptyRow, Field, PageHeader, Panel } from "@/components/common";
import { StatementDialog } from "@/components/StatementDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", email: "", phone: "", trn: "", address: "", emirate: "dubai" };

function ClientDialog({ open, onClose, initial, onSaved }) {
  const { t } = useLang();
  const [f, setF] = useState(EMPTY);
  useEffect(() => setF(initial || EMPTY), [initial, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { name: f.name, email: f.email, phone: f.phone, trn: f.trn, address: f.address, emirate: f.emirate };
      if (initial?.id) await api.put(`/clients/${initial.id}`, body);
      else await api.post("/clients", body);
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="client-dialog">
        <DialogHeader><DialogTitle className="font-display">{t(initial?.id ? "editClient" : "newClient")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <Field label={t("name")} className="col-span-2"><Input data-testid="client-name-input" required value={f.name} onChange={set("name")} /></Field>
          <Field label={t("email")}><Input data-testid="client-email-input" type="email" value={f.email} onChange={set("email")} /></Field>
          <Field label={t("phone")}><Input data-testid="client-phone-input" value={f.phone} onChange={set("phone")} /></Field>
          <Field label={`${t("trn")} (15)`}><Input data-testid="client-trn-input" className="font-mono" maxLength={15} value={f.trn} onChange={set("trn")} /></Field>
          <Field label={t("emirate")}><EmirateSelect data-testid="client-emirate-select" value={f.emirate} onChange={set("emirate")} /></Field>
          <Field label={t("address")} className="col-span-2"><Input data-testid="client-address-input" value={f.address} onChange={set("address")} /></Field>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel")}</Button>
            <Button data-testid="client-save-btn" className="bg-[#007A3D] hover:bg-[#005E2E]">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [stmt, setStmt] = useState(null);
  const load = () => api.get("/clients").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (c) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/clients/${c.id}`); toast.success(t("deleted")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const portalLink = async (c) => {
    try { const { data } = await api.post(`/clients/${c.id}/portal-link`); await navigator.clipboard.writeText(data.url); toast.success(t("portalLinkCopied")); } catch (e) { toast.error(errMsg(e)); }
  };
  const portalSend = async (c) => {
    try { await api.post(`/clients/${c.id}/portal/send`); toast.success(t("portalSent")); } catch (e) { toast.error(e?.response?.data?.detail?.code === "client_no_email" ? t("clientNoEmail") : errMsg(e)); }
  };
  return (
    <div data-testid="clients-page">
      <PageHeader title={t("clients")} subtitle={t("clientsSubtitle")}
        actions={<Button data-testid="new-client-btn" onClick={() => setDlg({ open: true, item: null })} className="bg-[#007A3D] hover:bg-[#005E2E]"><Plus className="me-1 h-4 w-4" />{t("newClient")}</Button>} />
      <Panel className="overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>{t("name")}</th><th>{t("trn")}</th><th>{t("emirate")}</th><th>{t("email")}</th><th>{t("phone")}</th><th className="text-end">{t("actions")}</th></tr></thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={6} />}
            {rows.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.id}`}>
                <td className="font-semibold text-slate-900">{c.name}</td>
                <td className="font-mono text-slate-600">{c.trn || "—"}</td>
                <td>{t(`em_${c.emirate}`)}</td>
                <td className="text-slate-600">{c.email}</td>
                <td className="text-slate-600">{c.phone}</td>
                <td className="text-end">
                  <button data-testid={`portal-link-client-${c.id}`} title={t("copyPortalLink")} onClick={() => portalLink(c)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100"><Link2 className="h-4 w-4" /></button>
                  <button data-testid={`portal-send-client-${c.id}`} title={t("sendPortalLink")} onClick={() => portalSend(c)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100"><Mail className="h-4 w-4" /></button>
                  <button data-testid={`statement-client-${c.id}`} title={t("statement")} onClick={() => setStmt(c)} className="rounded p-1.5 text-slate-500 hover:bg-emerald-50 hover:text-[#007A3D]"><FileText className="h-4 w-4" /></button>
                  <button data-testid={`edit-client-${c.id}`} onClick={() => setDlg({ open: true, item: c })} className="rounded p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-client-${c.id}`} onClick={() => remove(c)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-[#CE1126]"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <ClientDialog open={dlg.open} initial={dlg.item} onClose={() => setDlg({ open: false, item: null })} onSaved={() => { setDlg({ open: false, item: null }); load(); }} />
      {stmt && <StatementDialog client={stmt} onClose={() => setStmt(null)} />}
    </div>
  );
}
